import { RoleType } from '@hit-game/shared/enums';
import { Navigate, Route, Routes } from 'react-router-dom';

import { FullScreenLoader } from './components/common/FullScreenLoader';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ToastProvider } from './context/ToastContext';
import { AdminLayout } from './pages/AdminLayout';
import { UsersPage } from './pages/admin/UsersPage';
import { Login } from './pages/Login';
import { AccountsPage } from './pages/user/AccountsPage';
import { GroupPage } from './pages/user/GroupPage';
import { LobbyPage } from './pages/user/LobbyPage';
import { ProxyPage } from './pages/user/ProxyPage';
import { SettingsPage } from './pages/user/SettingsPage';
import { UserLayout } from './pages/UserLayout';

/** `/` — đưa về trang chủ theo vai trò, hoặc màn đăng nhập. */
function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;

  return <Navigate to={`/${user.role.toLowerCase()}`} replace />;
}

/**
 * Bọc 1 trang bằng guard vai trò + layout tương ứng.
 *
 * Bản cũ lặp nguyên khối `<ProtectedRoute><Layout><Page/></Layout></ProtectedRoute>`
 * cho từng route — 11 lần, và thêm route mới là chép tay lần nữa.
 */
function guarded(role: RoleType, page: React.ReactNode) {
  const Layout = role === RoleType.ADMIN ? AdminLayout : UserLayout;
  return (
    <ProtectedRoute allowedRoles={[role]}>
      <Layout>{page}</Layout>
    </ProtectedRoute>
  );
}

const USER_ROUTES = [
  { path: 'lobby', element: <LobbyPage /> },
  { path: 'accounts', element: <AccountsPage /> },
  { path: 'proxy', element: <ProxyPage /> },
  { path: 'group', element: <GroupPage /> },
  { path: 'settings', element: <SettingsPage /> },
];

const ADMIN_ROUTES = [{ path: 'users', element: <UsersPage /> }];

export default function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />

            {/* Trang chủ mỗi vai trò = trang đầu tiên trong danh sách của vai trò đó. */}
            <Route path="/user" element={guarded(RoleType.USER, <LobbyPage />)} />
            {USER_ROUTES.map(({ path, element }) => (
              <Route key={path} path={`/user/${path}`} element={guarded(RoleType.USER, element)} />
            ))}

            <Route path="/admin" element={guarded(RoleType.ADMIN, <UsersPage />)} />
            {ADMIN_ROUTES.map(({ path, element }) => (
              <Route
                key={path}
                path={`/admin/${path}`}
                element={guarded(RoleType.ADMIN, element)}
              />
            ))}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
